import { useEffect, useMemo, useState, type ReactNode } from "react";
import { PreferencesContext } from "./context";
import { applyPreferences, loadPreferences, preferenceStorageKey, resolveLanguage, resolveTheme, savePreferences } from "./storage";
import type { BloodPressureThresholds, LanguagePreference, Preferences, ThemePreference, WeightUnit } from "./types";

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<Preferences>(loadPreferences);
  const [environmentRevision, setEnvironmentRevision] = useState(0);
  void environmentRevision;
  const effective = { language: resolveLanguage(preferences), theme: resolveTheme(preferences) };

  useEffect(() => {
    applyPreferences(preferences);
    savePreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    const colorScheme = window.matchMedia("(prefers-color-scheme: dark)");
    const refreshSystemPreference = () => setEnvironmentRevision((revision) => revision + 1);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === preferenceStorageKey()) setPreferences(loadPreferences());
    };

    colorScheme.addEventListener("change", refreshSystemPreference);
    window.addEventListener("languagechange", refreshSystemPreference);
    window.addEventListener("storage", handleStorage);
    return () => {
      colorScheme.removeEventListener("change", refreshSystemPreference);
      window.removeEventListener("languagechange", refreshSystemPreference);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const value = useMemo(() => ({
    ...preferences,
    effectiveLanguage: effective.language,
    effectiveTheme: effective.theme,
    setLanguage: (language: LanguagePreference) => setPreferences((current) => ({ ...current, language })),
    setTheme: (theme: ThemePreference) => setPreferences((current) => ({ ...current, theme })),
    setWeightUnit: (weightUnit: WeightUnit) => setPreferences((current) => ({ ...current, weightUnit })),
    setBloodPressureThresholds: (bloodPressureThresholds: BloodPressureThresholds) => setPreferences((current) => ({ ...current, bloodPressureThresholds })),
  }), [effective.language, effective.theme, preferences]);

  return <PreferencesContext value={value}>{children}</PreferencesContext>;
}
